import { useCallback, useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  Flex,
  IconButton,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { IoClose, IoNotificationsOutline } from "react-icons/io5";
import { useTranslation } from "react-i18next";
import {
  getPushPermission,
  isPushEnabledLocally,
  isPushSupported,
} from "../services/pushNotifications";
import { isPushConfigured } from "../services/supabase/pushSubscriptions";
import { togglePushNotifications, pushNotificationsAvailable } from "./PushNotificationManager";
import { AuthContext } from "../store/auth-context";

const DISMISS_KEY = "payinapo_hide_push_prompt";

export default function PushNotificationPrompt({ visible }) {
  const { t } = useTranslation();
  const authCtx = useContext(AuthContext);
  const userId = authCtx.authenticatedUser?.id;

  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [busy, setBusy] = useState(false);

  const shellBg = useColorModeValue("rgba(255,255,255,0.96)", "rgba(18,18,20,0.96)");
  const shellBorder = useColorModeValue("rgba(15, 23, 42, 0.1)", "rgba(255, 255, 255, 0.08)");
  const titleColor = useColorModeValue("gray.900", "white");
  const mutedColor = useColorModeValue("gray.600", "whiteAlpha.700");

  const shouldShow =
    visible &&
    userId &&
    !dismissed &&
    pushNotificationsAvailable() &&
    getPushPermission() === "default" &&
    !isPushEnabledLocally();

  const dismiss = useCallback(() => {
    setDismissed(true);
    try {
      sessionStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* ignore */
    }
  }, []);

  const enable = useCallback(async () => {
    if (!userId || busy) return;
    setBusy(true);
    try {
      const result = await togglePushNotifications(userId, true);
      if (result.ok) dismiss();
    } finally {
      setBusy(false);
    }
  }, [userId, busy, dismiss]);

  useEffect(() => {
    if (!isPushSupported() || !isPushConfigured()) return;
    if (getPushPermission() !== "granted") return;
    if (isPushEnabledLocally()) return;
    if (!userId) return;
    togglePushNotifications(userId, true).catch(() => {});
  }, [userId]);

  if (!shouldShow) return null;

  return (
    <Box
      position="fixed"
      left={{ base: 3, sm: 4 }}
      right={{ base: 3, sm: 4 }}
      bottom="calc(84px + env(safe-area-inset-bottom, 0px))"
      zIndex={1100}
      maxW="420px"
      mx="auto"
      bg={shellBg}
      border="1px solid"
      borderColor={shellBorder}
      borderRadius="2xl"
      boxShadow="0 12px 40px rgba(0,0,0,0.28)"
      backdropFilter="blur(16px)"
      p={4}
    >
      <Flex align="flex-start" gap={3}>
        <Flex
          align="center"
          justify="center"
          w={10}
          h={10}
          borderRadius="xl"
          bg="brand.600"
          color="white"
          flexShrink={0}
        >
          <IoNotificationsOutline size={20} />
        </Flex>
        <Box flex={1} minW={0}>
          <Text fontWeight="semibold" color={titleColor} fontSize="sm" mb={1}>
            {t("push.promptTitle")}
          </Text>
          <Text fontSize="xs" color={mutedColor} lineHeight="tall" mb={3}>
            {t("push.promptBody")}
          </Text>
          <Flex gap={2}>
            <Button
              size="sm"
              h="36px"
              borderRadius="full"
              bg="brand.600"
              color="white"
              _hover={{ bg: "brand.500" }}
              onClick={enable}
              isLoading={busy}
            >
              {t("push.enable")}
            </Button>
            <Button size="sm" h="36px" borderRadius="full" variant="ghost" onClick={dismiss}>
              {t("push.notNow")}
            </Button>
          </Flex>
        </Box>
        <IconButton
          aria-label={t("common.close")}
          icon={<IoClose />}
          size="sm"
          variant="ghost"
          onClick={dismiss}
        />
      </Flex>
    </Box>
  );
}
